/**
 * Custom hook for managing dashboard data fetching and state management.
 * This hook handles the initialization and loading of shift data for either a venue
 * (for privileged users) or an employee, along with managing the time period
 * for which the data is fetched.
 *
 * @param {string} venueId - The ID of the venue to fetch shifts for
 * @param {string} employeeId - The ID of the employee to fetch shifts for (only used for non-privileged users)
 * @param {boolean} hasPrivilegedRole - Whether the current user has privileged access
 * @param {number} startOfWeekIndex - The index representing the start of the week (0 = Sunday, 1 = Monday, etc.)
 */
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';

import {
  setSelectedPeriod,
  setShiftsFromSnapshot,
  setEmployeeShiftsFromSnapshot,
  selectPeriod,
} from '../../../redux/shifts/shiftsSlice';
import { getWeekBoundaries } from '../../../utils/dateUtils';
import { setDailyTicketsFromSnapshot } from '../../../redux/dailyTickets/dailyTicketsSlice';
import { subscribeToDailyTickets } from '../../../redux/dailyTickets/dailyTicketsService';
import { subscribeToVenueShifts } from '../../../redux/shifts/shiftsService';
import { fetchShiftTypes } from '../../../redux/actions/shiftTypesActions';

export const useDashboardData = (venueId, employeeId, hasPrivilegedRole, startOfWeekIndex) => {
  const dispatch = useDispatch();
  const unsubAllShifts = useRef(null);
  const unsubEmployeeShifts = useRef(null);
  const unsubDailyTickets = useRef(null);

  const period = useSelector(selectPeriod);

  useEffect(() => {
    // Set the selected period to the current week, based on the venue's start of week
    if (!venueId) return;
    const dateNow = period?.start ?? new Date();
    const startDate = dayjs(dateNow).toDate();
    const { weekStart, weekEnd } = getWeekBoundaries(startDate, startOfWeekIndex);
    const currentPeriod = {
      start: weekStart.toISOString(),
      end: weekEnd.toISOString(),
    };

    // Set initial period only!
    if (!period?.start) {
      dispatch(setSelectedPeriod(currentPeriod));
    }

    // Fetch shift types for the venue
    dispatch(fetchShiftTypes());

    // Start all shifts listener and daily tickets listener for privileged users
    if (hasPrivilegedRole) {
      unsubAllShifts.current = subscribeToVenueShifts(venueId, currentPeriod, undefined, (shifts) =>
        dispatch(setShiftsFromSnapshot(shifts))
      );
      unsubDailyTickets.current = subscribeToDailyTickets(venueId, currentPeriod, (dailyTickets) =>
        dispatch(setDailyTicketsFromSnapshot(dailyTickets))
      );
    }

    // Start employee shifts listener for all users
    if (employeeId) {
      unsubEmployeeShifts.current = subscribeToVenueShifts(venueId, currentPeriod, employeeId, (shifts) =>
        dispatch(setEmployeeShiftsFromSnapshot(shifts))
      );
    }

    // Cleanup both listeners
    return () => {
      if (unsubAllShifts.current) unsubAllShifts.current();
      if (unsubEmployeeShifts.current) unsubEmployeeShifts.current();
      if (unsubDailyTickets.current) unsubDailyTickets.current();
    };
  }, [dispatch, period?.start, venueId, startOfWeekIndex, employeeId, hasPrivilegedRole]);
};
